import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import {
  LayoutDashboard, Workflow, Users, CreditCard, Settings, Building2,
  LogOut, Menu, X, ShieldCheck,
} from "lucide-react";

const ORG_NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/app/flows", label: "Flows", icon: Workflow, testid: "nav-flows" },
  { to: "/app/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/app/team", label: "Team", icon: ShieldCheck, testid: "nav-team", adminOnly: true },
  { to: "/app/billing", label: "Billing", icon: CreditCard, testid: "nav-billing", adminOnly: true },
  { to: "/app/settings", label: "Settings", icon: Settings, testid: "nav-settings", adminOnly: true },
];

const PLATFORM_NAV = [
  { to: "/platform", label: "Organizations", icon: Building2, end: true, testid: "nav-platform" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const isPlatform = user?.role === "platform_admin";
  const items = (isPlatform ? PLATFORM_NAV : ORG_NAV).filter(
    (i) => !i.adminOnly || user?.role === "org_admin"
  );

  const SideContent = () => (
    <div className="flex h-full flex-col">
      <div className="h-16 flex items-center px-6 border-b border-border">
        <Brand to={isPlatform ? "/platform" : "/app"} />
      </div>
      <nav className="flex-1 py-6 px-3 space-y-1">
        {isPlatform && (
          <p className="overline text-muted-foreground px-3 mb-3">Platform</p>
        )}
        {items.map((i) => (
          <NavLink
            key={i.to}
            to={i.to}
            end={i.end}
            onClick={() => setOpen(false)}
            data-testid={i.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors border-l-2 ${
                isActive
                  ? "bg-secondary text-foreground border-accent"
                  : "text-muted-foreground border-transparent hover:text-foreground hover:bg-secondary/50"
              }`
            }
          >
            <i.icon className="h-4 w-4" />
            {i.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-border p-4">
        <div className="mb-3 px-1">
          <p className="text-sm font-semibold truncate">{user?.name}</p>
          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
        </div>
        <button
          onClick={() => { logout(); nav("/login"); }}
          data-testid="logout-button"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium border border-border hover:bg-primary hover:text-primary-foreground transition-colors"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-card border-r border-border flex-col z-30">
        <SideContent />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-foreground/40" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 bg-card border-r border-border">
            <SideContent />
          </div>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="h-16 sticky top-0 z-20 bg-card/80 backdrop-blur-xl border-b border-border flex items-center justify-between px-4 lg:px-8">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-button">
            <Menu className="h-6 w-6" />
          </button>
          <div className="hidden lg:block overline text-muted-foreground">
            {isPlatform ? "Super-Admin Console" : "Workspace"}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs px-2.5 py-1 bg-secondary font-medium capitalize" data-testid="user-role-badge">
              {(user?.role || "").replace("_", " ")}
            </span>
          </div>
        </header>
        <main className="p-4 lg:p-8 max-w-[1400px]">{children}</main>
      </div>
    </div>
  );
}
